import {
  CaseDocumentWithPresignedUrl as Document,
  CasesWithDocumentView,
} from "pwapi";

import { CaseListDirection } from "./casesReducer";
import {
  CASE_OPEN_DOCUMENT_SUCCESS,
  DOCUMENTS_LIST_FAILURE,
  DOCUMENTS_LIST_REQUEST,
  DOCUMENTS_LIST_SUCCESS,
} from "../actions/constants";

export interface DocumentsListParams {
  reportType: Document.category;
  page: number;
  paginationPerPage: number;
  limit?: number;
  sortColumn?: string;
  sortDirection: CaseListDirection;

  fields?: string[];
}

export interface CasesWithDocumentsState {
  count: number;
  next?: string;
  previous?: string;
  results: CasesWithDocumentView[];
  lastRequestTimestamp: number;
  loading: boolean;
  error?: Error;
  currentCaseSearch: DocumentsListParams;
}

function initialFormState(): DocumentsListParams {
  const defaultPageSize = localStorage.getItem("documentListDefaultPageSize");
  return {
    reportType: Document.category.CASE_REPORT,
    page: 1,
    paginationPerPage: defaultPageSize ? JSON.parse(defaultPageSize) : 10,
    sortDirection: CaseListDirection.Asc,
    sortColumn: "received_date",
  };
}

export const initialState: CasesWithDocumentsState = {
  results: [],
  count: 0,
  lastRequestTimestamp: 0,
  loading: true,
  error: undefined,
  currentCaseSearch: initialFormState(),
};

const curateDocuments = (
  caseDocumentView: CasesWithDocumentView,
  category?: Document.category
) => {
  let caseDocumentViews = caseDocumentView.case_document_views;
  let caseReport = undefined;

  // Get Requisition form documents, case report is ignored
  if (category === Document.category.REQUISITION_FORM) {
    caseDocumentViews = caseDocumentViews?.filter(
      (doc) =>
        doc.category === Document.category.REQUISITION_FORM ||
        doc.category === Document.category.CLINIC_NOTES ||
        doc.category === Document.category.CONSENT_FORM ||
        doc.category === Document.category.DOC_CLINICAL ||
        doc.category === Document.category.DOC_INSURANCE ||
        doc.category === Document.category.PATIENT_DEMOGRAPHICS
    );
  } else {
    // if the category is not requisition form specifically, filter for the most current non-pending case report
    caseReport = caseDocumentView.case_document_views
      ?.filter(
        (doc) =>
          doc.category === Document.category.CASE_REPORT &&
          doc.status !== Document.status.PENDING
      )
      ?.sort((a, b) =>
        (a.created_at || "") > (b.created_at || "") ? -1 : 1
      )?.[0];

    // After getting the most current case report, remove all Case Report documents from the list
    caseDocumentViews = caseDocumentViews?.filter(
      (doc) => doc.category !== Document.category.CASE_REPORT
    );
  }

  // Get Case Report documents
  if (category === Document.category.CASE_REPORT) {
    caseDocumentViews = caseDocumentViews?.filter(
      (doc) =>
        doc.category === Document.category.CONSULTATION_REPORT ||
        doc.category === Document.category.LAB_REPORT ||
        doc.category === Document.category.DIF_REPORT
    );
  }

  // return the final document list, curated to the above constraints
  return {
    ...caseDocumentView,
    case_document_views: [
      ...(caseDocumentViews || []),
      ...(!!caseReport ? [caseReport] : []),
    ],
  };
};

const reducer = (
  state: CasesWithDocumentsState = initialState,
  action: any
): CasesWithDocumentsState => {
  switch (action.type) {
    case CASE_OPEN_DOCUMENT_SUCCESS:
      return state &&
        !action.payload.user?.is_superuser &&
        action.payload.source === "document-list"
        ? {
            ...state,
            results: state.results
              ? state.results.map((result) => {
                  return result.id === action.payload.case_id
                    ? {
                        ...result,
                        case_document_views: result.case_document_views
                          ? result.case_document_views.map((view: any) => {
                              return view.id === action.payload.id
                                ? {
                                    ...view,
                                    has_been_viewed: true,
                                  }
                                : view;
                            })
                          : result.case_document_views,
                      }
                    : result;
                })
              : state.results,
          }
        : state;
    case DOCUMENTS_LIST_REQUEST:
      return state
        ? {
            ...state,
            loading: true,
          }
        : state;
    case DOCUMENTS_LIST_FAILURE:
      return state
        ? {
            ...initialState,
            loading: false,
          }
        : state;
    case DOCUMENTS_LIST_SUCCESS:
      return state
        ? {
            ...{
              ...action.payload,
              results: action.payload.results?.map((docs: any) =>
                curateDocuments(docs, action.payload.category)
              ),
            },
            loading: false,
          }
        : state;
    default:
      return state;
  }
};

export default reducer;
